import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, Inbox, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

function ThreadList({ threads, activeId }) {
  if (threads.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground text-sm border border-border rounded-sm bg-card">
        <Inbox className="w-7 h-7 mx-auto mb-3 text-primary" strokeWidth={1.5} />
        No messages yet.
      </div>
    );
  }
  return (
    <div className="divide-y divide-border border border-border rounded-sm bg-card">
      {threads.map((t) => (
        <Link
          key={t.thread_id}
          to={`/messages/${t.other_user_id}`}
          data-testid={`thread-${t.other_user_id}`}
          className={`block p-5 hover:bg-secondary/40 transition-colors ${activeId === t.other_user_id ? "bg-secondary/60" : ""}`}
        >
          <div className="flex justify-between items-baseline">
            <div className="font-serif text-lg">{t.other_user_name}</div>
            {t.unread_count > 0 && (
              <span className="bg-primary text-primary-foreground text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-semibold">
                {t.unread_count}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-1 truncate">{t.last_message}</div>
          <div className="text-[10px] text-muted-foreground tracking-wider uppercase mt-2">
            {new Date(t.last_at).toLocaleDateString()}
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function Messages() {
  const { otherId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [thread, setThread] = useState(null);
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data);
    } catch {}
  };

  const loadThread = async (id) => {
    if (!id) { setThread(null); return; }
    try {
      const { data } = await api.get(`/messages/thread/${id}`);
      setThread(data);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch {
      setThread(null);
    }
  };

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { loadThread(otherId); }, [otherId]);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/messages", { to_user_id: otherId, content });
      setContent("");
      await loadThread(otherId);
      await loadThreads();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="messages-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
      <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-4">◦ Inbox</div>
      <h1 className="font-serif text-4xl lg:text-5xl tracking-tight mb-12">Messages</h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className={`lg:col-span-4 ${otherId ? "hidden lg:block" : ""}`}>
          <ThreadList threads={threads} activeId={otherId} />
        </aside>

        <main className="lg:col-span-8">
          {!otherId ? (
            <div className="border border-border p-12 rounded-sm bg-card text-center text-muted-foreground">
              Select a conversation, or message a seller from their profile.
            </div>
          ) : !thread ? (
            <div className="text-muted-foreground">Loading…</div>
          ) : (
            <div className="border border-border rounded-sm bg-card flex flex-col h-[600px]">
              <div className="p-5 border-b border-border flex items-center gap-3">
                <button onClick={() => navigate("/messages")} className="lg:hidden text-muted-foreground hover:text-foreground">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div>
                  <div className="font-serif text-xl">{thread.other_user?.name}</div>
                  {thread.other_user?.role === "seller" && (
                    <Link to={`/shop/${thread.other_user.id}`} className="text-xs text-muted-foreground hover:text-primary">
                      View profile →
                    </Link>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="message-list">
                {thread.messages.map((m) => {
                  const mine = m.from_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-4 py-3 rounded-sm text-sm leading-relaxed ${mine ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                        <div>{m.content}</div>
                        <div className={`text-[10px] mt-1.5 ${mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                          {new Date(m.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="border-t border-border p-4 flex gap-3">
                <input
                  data-testid="message-thread-input"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Write a message…"
                  className="flex-1 bg-transparent border border-border rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-primary"
                />
                <button data-testid="thread-send-btn" disabled={sending || !content.trim()} className="btn-primary px-5 py-2.5 text-sm rounded-sm inline-flex items-center gap-2 disabled:opacity-60">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
